import {
  buildFallbackLoginPageUrl,
  buildLoginPageURL,
  extractOrigin
} from '../components/utils/Login'
import { useEffect, useRef, useState } from 'react'
import { buildLoginPopupFeatures } from '../utils/loginPopup'

/**
 * In the i3 desktop environment, window.open() causes all components to remount,
 * resulting in the loss of component state, including the reference to the opened login popup.
 * By storing this reference in the global scope, we can retain it across remounts.
 */
let persistentLoginPopup = null

export const useLogin = () => {
  const [loginPopup, setLoginPopup] = useState(null)
  const intervalRef = useRef(null)

  const setLoginPopupWithPersistence = (popup) => {
    setLoginPopup(popup)
    persistentLoginPopup = popup
  }

  const checkPopupClosed = () => {
    if (loginPopup?.closed && intervalRef.current) {
      clearInterval(intervalRef)
      setLoginPopupWithPersistence(null)
    }
  }

  const login = ({ loginLocation, clientId }) => {
    const loginUrl = buildLoginPageURL({
      clientId,
      loginLocation
    })

    if (persistentLoginPopup && !persistentLoginPopup.closed) {
      persistentLoginPopup.focus()
      return
    }

    const windowFeatures = buildLoginPopupFeatures()
    const popup = window.open(loginUrl, '_blank', windowFeatures)

    if (!popup) {
      window.location.href = buildFallbackLoginPageUrl({
        clientId,
        loginLocation
      })
      return
    }

    setLoginPopupWithPersistence(popup)
    intervalRef.current = setInterval(checkPopupClosed, 50)
  }

  useEffect(() => {
    const currentOrigin = extractOrigin(window.location.href)

    const handleMessage = (event) => {
      if (event.origin !== currentOrigin) {
        return
      }

      try {
        if (event.data === 'login_success' && persistentLoginPopup) {
          persistentLoginPopup.postMessage('confirm', currentOrigin)
          location.reload()
        }
      } catch (e) {}
    }

    window.addEventListener('message', handleMessage, false)

    return () => {
      window.removeEventListener('message', handleMessage, false)

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [loginPopup])

  return { login }
}
